import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { AlertTriangle, ChevronRight, Coins, Loader2, Pencil, Save, Trash2 } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import RoleGuard from '@/components/auth/RoleGuard';
import { EmptyState } from '@/features/qa-reports/components/EmptyState';
import {
  RankedSupplierCostList,
  RANKED_SUPPLIER_COLS,
  type SupplierCostRow,
} from '@/features/inventory/components/RankedSupplierCostList';
import { findCostOfGoodsSaveBlocker } from '@/features/inventory/utils/cost-of-goods-save-validation';
import { useCostOfGoods } from '@/features/inventory/hooks/useCostOfGoods';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { PageLoader } from '@/components/loading/PageLoader';
import type { CostCurrency } from '@threadheads/shared';

const COST_CURRENCY_OPTIONS: CostCurrency[] = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'];

/** The per-supplier cost shape emitted on save (numeric unitCost). */
type SupplierCostPayload = {
  supplierId: string;
  rank: number;
  unitCost: number;
  currency: CostCurrency;
};

type CostRow = {
  productType: string;
  productCount: number;
  skuCount: number;
  factoryCost: string;
  factoryCostCurrency: 'USD' | 'AUD';
  landedCost: string;
  landedCostCurrency: CostCurrency;
  isOrphaned: boolean;
  hasCost: boolean;
  // BLU-271: category-level supplier defaults (rank-1/rank-2 mirror).
  defaultSupplierId: string | null;
  backupSupplierId: string | null;
  // BLU-599: ranked suppliers, each with its own cost + currency. Rank-1's cost
  // is the category's standard/landed cost. unitCost is a string for input state.
  suppliers: SupplierCostRow[];
};

/** The load-time item shape, widened with the BLU-599 `suppliers` field which
 *  the backend adds in parallel (tolerated as optional until its types land). */
type CostItem = ReturnType<typeof useCostOfGoods>['items'][number] & {
  suppliers?: Array<{ supplierId: string; rank: number; unitCost: number; currency: CostCurrency }>;
};

/** Diff two ranked-supplier arrays. Currency is ignored when apply-to-all owns
 *  it (mirrors the scalar-only diff the page uses for landed cost). */
const suppliersDiffer = (
  a: SupplierCostRow[],
  b: SupplierCostRow[],
  ignoreCurrency: boolean,
): boolean => {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (!x || !y) return true;
    if (x.supplierId !== y.supplierId) return true;
    if (x.rank !== y.rank) return true;
    if (x.unitCost !== y.unitCost) return true;
    if (!ignoreCurrency && x.currency !== y.currency) return true;
  }
  return false;
};

export default function CostOfGoods() {
  return (
    <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
      <CostOfGoodsContent />
    </RoleGuard>
  );
}

function CostOfGoodsContent() {
  const { t } = useTranslation('inventory');
  const { items, loading, upsertCostOfGoods, deleteCostOfGoods } = useCostOfGoods();
  const { suppliers } = useSuppliers({ includeInactive: true });
  const sortedSuppliers = useMemo(
    () => [...suppliers].sort((a, b) => a.title.localeCompare(b.title)),
    [suppliers]
  );
  const { organization } = useOrganization();
  const orgCurrency = (organization?.default_currency ?? 'AUD') as CostCurrency;
  const { format } = useCurrencyFormatter();

  usePageTitle(t('costOfGoods.title'), t('costOfGoods.subtitle'));

  const [rows, setRows] = useState<CostRow[]>([]);
  const [initialRows, setInitialRows] = useState<CostRow[]>([]);
  const [orphanedExpanded, setOrphanedExpanded] = useState(false);
  const [howCostsExpanded, setHowCostsExpanded] = useState(false);

  // BLU-388 redesign: page-level "apply to all" mode + warning modal state.
  const [applyToAll, setApplyToAll] = useState(false);
  const [pageCurrency, setPageCurrency] = useState<CostCurrency>(orgCurrency);
  const [initialPageCurrency, setInitialPageCurrency] = useState<CostCurrency>(orgCurrency);
  const [editCurrencyOpen, setEditCurrencyOpen] = useState(false);
  const [pendingPageCurrency, setPendingPageCurrency] = useState<CostCurrency>(orgCurrency);

  useEffect(() => {
    if (loading) return;
    const built: CostRow[] = (items as CostItem[]).map((item) => {
      const landedCostCurrency =
        (item.landedCostCurrency as CostCurrency | undefined) ?? orgCurrency;

      // Map the ranked suppliers from the backend. If the field is absent
      // (backend types not landed yet) or empty, synthesise a rank-1/rank-2
      // list from the BLU-271 default/backup columns so the editor is never
      // blank during the transition and rank-1 mirrors the existing landed
      // cost. When even that is absent, seed one empty primary row so the
      // operator has somewhere to start.
      let supplierRows: SupplierCostRow[] = (item.suppliers ?? []).map((s) => ({
        uid: crypto.randomUUID(),
        supplierId: s.supplierId,
        rank: s.rank,
        unitCost: s.unitCost != null ? String(s.unitCost) : '',
        currency: (s.currency as CostCurrency | undefined) ?? landedCostCurrency,
      }));
      if (supplierRows.length === 0 && item.defaultSupplierId) {
        supplierRows = [
          {
            uid: crypto.randomUUID(),
            supplierId: item.defaultSupplierId,
            rank: 1,
            unitCost: item.landedCost != null ? String(item.landedCost) : '',
            currency: landedCostCurrency,
          },
        ];
        if (item.backupSupplierId) {
          supplierRows.push({
            uid: crypto.randomUUID(),
            supplierId: item.backupSupplierId,
            rank: 2,
            unitCost: item.landedCost != null ? String(item.landedCost) : '',
            currency: landedCostCurrency,
          });
        }
      }
      if (supplierRows.length === 0) {
        supplierRows = [
          {
            uid: crypto.randomUUID(),
            supplierId: null,
            rank: 1,
            unitCost: item.landedCost != null ? String(item.landedCost) : '',
            currency: landedCostCurrency,
          },
        ];
      }
      // Sort by stored rank, then resequence to a dense contiguous 1..N so a
      // non-contiguous stored set (e.g. a lone rank-2 after the default supplier
      // was deleted) makes the min-rank row the primary — no dup-rank collision,
      // no false "Missing cost".
      supplierRows.sort((a, b) => a.rank - b.rank);
      supplierRows = supplierRows.map((s, i) => ({ ...s, rank: i + 1 }));

      return {
        productType: item.productType,
        productCount: item.productCount,
        skuCount: item.skuCount,
        factoryCost: item.factoryCost != null ? String(item.factoryCost) : '',
        factoryCostCurrency: item.factoryCostCurrency ?? 'USD',
        landedCost: item.landedCost != null ? String(item.landedCost) : '',
        landedCostCurrency,
        isOrphaned: item.isOrphaned,
        hasCost: item.hasCost,
        defaultSupplierId: item.defaultSupplierId ?? null,
        backupSupplierId: item.backupSupplierId ?? null,
        suppliers: supplierRows,
      };
    });
    setRows(built);
    setInitialRows(built);

    // Default the page-level currency to the catalog's if it's uniformly priced
    // — now derived from the ranked supplier currencies rather than the single
    // per-category landed currency.
    const distinctCurrencies = new Set(
      built.flatMap((r) => r.suppliers.map((s) => s.currency)),
    );
    const isUniform = distinctCurrencies.size === 1 && distinctCurrencies.size > 0;
    const initial: CostCurrency = isUniform
      ? ([...distinctCurrencies][0] ?? orgCurrency)
      : orgCurrency;
    setPageCurrency(initial);
    setInitialPageCurrency(initial);
    setPendingPageCurrency(initial);

    // Apply-to-all is the default view when the catalog is uniformly priced
    // (BLU-388): one currency badge, edit costs. Per-row currency is the opt-in.
    if (isUniform) {
      setApplyToAll(true);
    }
  }, [items, loading, orgCurrency]);

  const activeRows = useMemo(() => rows.filter((r) => !r.isOrphaned), [rows]);
  const orphanedRows = useMemo(() => rows.filter((r) => r.isOrphaned), [rows]);

  const isDirty = useMemo(() => {
    if (rows.length !== initialRows.length) return true;
    if (applyToAll && pageCurrency !== initialPageCurrency) return true;
    return rows.some((row, i) => {
      const init = initialRows[i];
      if (!init) return true;
      return (
        row.factoryCost !== init.factoryCost ||
        row.factoryCostCurrency !== init.factoryCostCurrency ||
        suppliersDiffer(row.suppliers, init.suppliers, applyToAll)
      );
    });
  }, [rows, initialRows, applyToAll, pageCurrency, initialPageCurrency]);

  const handleSuppliersChange = (productType: string, next: SupplierCostRow[]) => {
    setRows((prev) =>
      prev.map((row) => (row.productType === productType ? { ...row, suppliers: next } : row)),
    );
  };

  const handleReset = () => {
    setRows(initialRows);
    setPageCurrency(initialPageCurrency);
    setPendingPageCurrency(initialPageCurrency);
  };

  const handleSave = async () => {
    // Validate: a picked supplier must carry a valid cost, and a cost the operator
    // *entered* must not be stranded without a supplier (buildSuppliers would drop
    // it). Untouched seeded legacy costs (a category with a landed cost but no
    // per-category supplier yet) are excluded so they don't brick the save — see
    // findCostOfGoodsSaveBlocker.
    const blocker = findCostOfGoodsSaveBlocker(rows, initialRows);
    if (blocker) {
      toast({
        title: t('costOfGoods.invalidCost'),
        description: t('costOfGoods.landedCostInvalid', { type: blocker.productType }),
        variant: 'destructive',
      });
      return;
    }

    const buildSuppliers = (row: CostRow): SupplierCostPayload[] =>
      row.suppliers
        .filter((s) => s.supplierId)
        .map((s, i) => ({
          supplierId: s.supplierId as string,
          rank: i + 1,
          unitCost: Number(s.unitCost),
          currency: applyToAll ? pageCurrency : s.currency,
        }));

    const updates = rows
      .filter((row, i) => {
        if (applyToAll) return true;
        const init = initialRows[i];
        if (!init) return true;
        return (
          row.factoryCost !== init.factoryCost ||
          row.factoryCostCurrency !== init.factoryCostCurrency ||
          suppliersDiffer(row.suppliers, init.suppliers, false)
        );
      })
      .map((row) => {
        const payloadSuppliers = buildSuppliers(row);
        const primary = payloadSuppliers[0];
        const backup = payloadSuppliers[1];
        const factoryCost = row.factoryCost.trim() !== '' ? Number(row.factoryCost) : undefined;
        return {
          productType: row.productType,
          // Rank-1 drives the standard/landed cost (backend RPC writes it too;
          // sent here for payload coherence).
          landedCost: primary ? primary.unitCost : undefined,
          landedCostCurrency: primary
            ? primary.currency
            : applyToAll
              ? pageCurrency
              : row.landedCostCurrency,
          factoryCost,
          factoryCostCurrency: row.factoryCostCurrency,
          defaultSupplierId: primary?.supplierId ?? null,
          backupSupplierId: backup?.supplierId ?? null,
          suppliers: payloadSuppliers,
        };
      });

    const invalidFactory = updates.find(
      (r) => r.factoryCost !== undefined && (Number.isNaN(r.factoryCost) || r.factoryCost < 0)
    );
    if (invalidFactory) {
      toast({
        title: t('costOfGoods.invalidCost'),
        description: t('costOfGoods.factoryCostInvalid', { type: invalidFactory.productType }),
        variant: 'destructive',
      });
      return;
    }

    if (updates.length === 0) {
      toast({ title: t('costOfGoods.nothingToSave'), description: t('costOfGoods.enterAtLeastOneCost') });
      return;
    }

    try {
      await upsertCostOfGoods.mutateAsync({ costs: updates });
    } catch {
      // Errors handled in hook
    }
  };

  const handleDeleteOrphaned = async () => {
    const types = orphanedRows.map((r) => r.productType);
    if (types.length === 0) return;
    try {
      await deleteCostOfGoods.mutateAsync({ productTypes: types });
    } catch {
      // Errors handled in hook
    }
  };

  // Confirm modal: commit the pending page-level currency. Updates the page-level
  // state AND every ranked supplier row's currency so the whole page reads
  // consistently. Save still requires the sticky bar.
  const handleConfirmPageCurrency = () => {
    setPageCurrency(pendingPageCurrency);
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        landedCostCurrency: pendingPageCurrency,
        suppliers: row.suppliers.map((s) => ({ ...s, currency: pendingPageCurrency })),
      })),
    );
    setEditCurrencyOpen(false);
  };

  // Toggle: turning ON snaps every supplier row to the page currency; turning
  // OFF restores each category's saved-from-DB currencies.
  const handleApplyToAllChange = (next: boolean) => {
    if (next) {
      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          landedCostCurrency: pageCurrency,
          suppliers: row.suppliers.map((s) => ({ ...s, currency: pageCurrency })),
        })),
      );
    } else {
      setRows((prev) =>
        prev.map((row) => {
          const init = initialRows.find((r) => r.productType === row.productType);
          return {
            ...row,
            landedCostCurrency: init?.landedCostCurrency ?? orgCurrency,
            suppliers: row.suppliers.map((s, i) => ({
              ...s,
              currency: init?.suppliers[i]?.currency ?? init?.landedCostCurrency ?? orgCurrency,
            })),
          };
        }),
      );
    }
    setApplyToAll(next);
  };

  return (
    <>
      <PageActionBar
        ariaLabel="Cost of goods actions"
        left={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Switch
                id="apply-to-all-switch"
                checked={applyToAll}
                onCheckedChange={handleApplyToAllChange}
              />
              <Label
                htmlFor="apply-to-all-switch"
                className="text-xs font-medium select-none cursor-pointer"
              >
                Apply one currency to every category
              </Label>
            </div>
            {applyToAll && (
              <>
                <PageActionBarDivider />
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="muted"
                    className="font-mono text-[11px] uppercase tracking-wide tabular-nums"
                  >
                    {pageCurrency}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setPendingPageCurrency(pageCurrency);
                      setEditCurrencyOpen(true);
                    }}
                    aria-label={`Change page-wide currency (currently ${pageCurrency})`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </>
            )}
          </div>
        }
      />

      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        {/* How costs work — collapsible, auto-collapsed */}
        <div className="mb-6 rounded-lg border bg-muted/40">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted/60 transition-colors"
            onClick={() => setHowCostsExpanded((v) => !v)}
          >
            <ChevronRight className={cn(
              'h-4 w-4 transition-transform duration-200',
              howCostsExpanded && 'rotate-90'
            )} />
            How costs work
          </button>
          {howCostsExpanded && (
            <div className="border-t px-4 py-3 text-sm text-muted-foreground">
              <p className="mb-2">Costs apply in this order (highest priority first):</p>
              <ol className="list-decimal list-inside space-y-2.5">
                <li>
                  <strong>Weighted Average Cost:</strong> Calculated automatically when you receive purchase orders. Blends your existing stock value with new receipts at their landed cost (including freight, customs, etc). Always used when available.
                  <p className="ml-5 mt-1 text-muted-foreground">Example: you have 100 units at $12.00 each, then receive 50 more at $15.50. Your new cost per unit is $12.83.</p>
                </li>
                <li><strong>{t('costOfGoods.howDefaultLabel')}</strong> {t('costOfGoods.howDefaultBody')}</li>
              </ol>
              <p className="mt-3">{t('costOfGoods.howSupplierPrices')}</p>
            </div>
          )}
        </div>

        {loading ? (
          <PageLoader pool="costOfGoods" />
        ) : activeRows.length === 0 && orphanedRows.length === 0 ? (
          <EmptyState
            icon={Coins}
            title={t('costOfGoods.noProductTypes')}
            description={t('costOfGoods.emptyDesc')}
          />
        ) : (
          <>
            {/* Orphaned entries — between explainer and table */}
            {orphanedRows.length > 0 && (
              <div className="mb-6 rounded-md border border-amber-200 bg-amber-50/50">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-amber-800 hover:bg-amber-50 transition-colors"
                  onClick={() => setOrphanedExpanded((v) => !v)}
                >
                  <ChevronRight className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    orphanedExpanded && 'rotate-90'
                  )} />
                  {t('costOfGoods.orphanedTypes', { count: orphanedRows.length })}
                  <span className="font-normal text-amber-600 ml-1">
                    {t('costOfGoods.orphanedDesc')}
                  </span>
                </button>
                {orphanedExpanded && (
                  <div className="border-t border-amber-200 px-4 py-3">
                    <div className="rounded-md border border-amber-100 bg-white">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left font-medium px-4 py-2.5">{t('costOfGoods.productType')}</th>
                            <th className="text-left font-medium px-4 py-2.5 w-40">Category cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanedRows.map((row) => (
                            <tr key={row.productType} className="border-b last:border-b-0">
                              <td className="px-4 py-2 text-amber-800">{row.productType}</td>
                              <td className="px-4 py-2 text-muted-foreground tabular-nums">
                                {row.landedCost.trim() !== ''
                                  ? format(Number(row.landedCost), row.landedCostCurrency)
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteOrphaned}
                        disabled={deleteCostOfGoods.isPending}
                        className="text-amber-700 border-amber-300 hover:bg-amber-100"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        {deleteCostOfGoods.isPending
                          ? 'Removing...'
                          : `Remove ${orphanedRows.length} orphaned ${orphanedRows.length === 1 ? 'entry' : 'entries'}`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Suppliers by product type: grouped rows, a category header
                (type, product and SKU counts), then its ranked supplier
                sub-rows. */}
            {activeRows.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-lg font-semibold">{t('costOfGoods.sectionTitle')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('costOfGoods.sectionDesc')}
                    {applyToAll && (
                      <>
                        {' '}
                        Every category will save in <span className="font-mono uppercase font-medium">{pageCurrency}</span> while this option is on.
                      </>
                    )}
                  </p>
                </div>
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50 text-left">
                        <th className="px-4 py-3 font-medium w-32">
                          <span className="sr-only">Supplier rank</span>
                        </th>
                        <th className="px-4 py-3 font-medium">Supplier</th>
                        <th className="px-4 py-3 font-medium w-64">
                          Landed cost
                          {applyToAll && (
                            <span className="ml-1.5 font-normal text-xs text-muted-foreground">
                              ({pageCurrency})
                            </span>
                          )}
                        </th>
                        <th className="px-4 py-3 font-medium w-44">
                          <span className="sr-only">Row actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeRows.map((row) => {
                        return (
                          <Fragment key={row.productType}>
                            {/* Category header row: full-colspan colgroup cell
                                with the type and its counts. */}
                            <tr className="border-b bg-muted/30">
                              <th
                                scope="rowgroup"
                                colSpan={RANKED_SUPPLIER_COLS}
                                className="px-4 py-2.5 text-left font-normal"
                              >
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                  <span className="font-medium">{row.productType}</span>
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <Badge variant="muted" size="sm" className="tabular-nums">
                                      {row.productCount}
                                    </Badge>
                                    products
                                  </span>
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <Badge variant="muted" size="sm" className="tabular-nums">
                                      {row.skuCount}
                                    </Badge>
                                    SKUs
                                  </span>
                                </div>
                              </th>
                            </tr>
                            <RankedSupplierCostList
                              rows={row.suppliers}
                              onChange={(next) => handleSuppliersChange(row.productType, next)}
                              suppliers={sortedSuppliers}
                              applyToAll={applyToAll}
                              pageCurrency={pageCurrency}
                              category={row.productType}
                              disabled={upsertCostOfGoods.isPending}
                            />
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sticky save bar — matches StockSyncSettings pattern */}
            {isDirty && (
              <div className="sticky bottom-4 z-10 flex justify-center mt-6">
                <div className="inline-flex items-center gap-4 rounded-lg bg-primary px-5 py-3 text-primary-foreground shadow-lg">
                  <p className="text-sm text-primary-foreground/80">Unsaved changes</p>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={handleReset} disabled={upsertCostOfGoods.isPending}>
                      Cancel
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleSave} disabled={upsertCostOfGoods.isPending}>
                      {upsertCostOfGoods.isPending
                        ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Saving...</>
                        : <><Save className="mr-1.5 h-3.5 w-3.5" />Save Changes</>}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Page-wide currency change confirmation. Uses DialogPrimitive directly
          so we can override the overlay with a real backdrop blur (the shared
          DialogContent ships an opaque-black overlay). */}
      <DialogPrimitive.Root open={editCurrencyOpen} onOpenChange={setEditCurrencyOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              'fixed inset-0 z-50 bg-background/60 backdrop-blur-md',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
              'rounded-lg border border-destructive/30 bg-background shadow-2xl',
              'data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out',
            )}
          >
            <div className="flex items-start gap-3 border-b border-destructive/20 bg-destructive/5 px-5 py-4 rounded-t-lg">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="space-y-1">
                <DialogPrimitive.Title className="text-base font-semibold leading-tight text-destructive">
                  Change every category's default currency?
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-sm text-muted-foreground leading-snug">
                  This sets the currency tag on every supplier cost. The numbers don't change: a cost of $5.00 stays $5.00, but it now reads as the new currency. After switching, edit any cost whose number needs to change.
                </DialogPrimitive.Description>
              </div>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="page-currency-select" className="text-xs font-medium">
                  New currency for every category
                </Label>
                <Select
                  value={pendingPageCurrency}
                  onValueChange={(value) => setPendingPageCurrency(value as CostCurrency)}
                >
                  <SelectTrigger
                    id="page-currency-select"
                    className="h-9 w-full font-mono text-sm uppercase"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_CURRENCY_OPTIONS.map((code) => (
                      <SelectItem key={code} value={code} className="font-mono uppercase">
                        {code}
                        {code === orgCurrency && (
                          <span className="ml-2 text-[10px] text-muted-foreground normal-case">
                            org base
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pendingPageCurrency !== orgCurrency && (
                <p className="text-[12px] leading-snug text-muted-foreground">
                  Reports and stock valuations across the app will keep showing in <span className="font-mono uppercase font-medium">{orgCurrency}</span>. Conversion uses the latest exchange rate.
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-5 py-3 rounded-b-lg">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditCurrencyOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleConfirmPageCurrency}
                disabled={pendingPageCurrency === pageCurrency}
              >
                Switch to {pendingPageCurrency}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
